import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const diferenciais = [
  { icon: 'ri-medal-line', text: 'Mais de 33 anos de experiência no mercado' },
  { icon: 'ri-building-2-line', text: 'Fundador da IBX Brasil' },
  { icon: 'ri-shield-check-line', text: 'Especialista em análise de riscos e negociação de seguros' },
  { icon: 'ri-group-line', text: 'Método aplicado e validado por milhares de segurados' },
  { icon: 'ri-eye-line', text: 'Foco em transparência, critério técnico e proteção real' },
];

export default function AutorMetodo() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation();
  const { ref: textRef, isVisible: textVisible } = useScrollAnimation();
  const { ref: listRef, visibleItems } = useStaggerAnimation(diferenciais.length);
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section
      id="autor-metodo"
      className="relative bg-white py-12 sm:py-16 lg:py-20 overflow-hidden"
      aria-labelledby="autor-metodo-heading"
    >
      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[560px] lg:min-h-[680px]">

          {/* ── IMAGEM (ESQUERDA) ── */}
          <div
            ref={imgRef as React.RefObject<HTMLDivElement>}
            className={`relative hidden lg:block h-full min-h-[420px] sm:min-h-[560px] lg:min-h-[680px] mt-4 lg:mt-0 transition-all duration-700 ease-out ${
              imgVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">
              <img
                src="/imagens/secoes/autor-metodo-1.jpg"
                alt="Paulo Cardoso, especialista em gestão de riscos e fundador da IBX Brasil"
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
                width="800"
                height="900"
              />

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/10" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-petrol-900/40 via-transparent to-transparent" aria-hidden="true" />

              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 border border-teal-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl shrink-0">
                      <i className="ri-user-star-line text-teal-600 text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-[10px] text-teal-600 font-extrabold uppercase tracking-widest mb-0.5">
                        Criador do Método
                      </p>
                      <p className="text-sm font-bold text-petrol-900 leading-tight">Paulo Cardoso</p>
                      <p className="text-xs text-petrol-500 mt-0.5">Fundador da IBX Brasil · 33 anos de mercado</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute top-6 left-6">
                <div className="bg-teal-600/90 backdrop-blur-sm rounded-xl px-4 py-2.5 shadow-lg border border-teal-500/50">
                  <p className="text-white text-xs font-bold uppercase tracking-widest">+33 anos</p>
                  <p className="text-teal-100 text-[10px] leading-tight">de atuação prática</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── TEXTO (DIREITA) ── */}
          <div
            ref={textRef as React.RefObject<HTMLDivElement>}
            className={`flex flex-col justify-center lg:pr-8 transition-all duration-700 ease-out ${
              textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div
              ref={headerRef as React.RefObject<HTMLDivElement>}
              className={`transition-all duration-700 ease-out ${
                headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 px-4 py-2 rounded-full mb-5 w-fit">
                <i className="ri-user-heart-line text-teal-600 text-sm" aria-hidden="true" />
                <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                  Quem está por trás do Método
                </span>
              </div>
            </div>

            <h2
              id="autor-metodo-heading"
              className="text-2xl sm:text-3xl lg:text-[2.1rem] xl:text-4xl font-bold text-petrol-900 mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Quem está por trás do{' '}
              <span className="text-teal-600">Método de Contratação Ativa</span>
            </h2>

            <p className="text-sm sm:text-base lg:text-lg text-petrol-600 leading-relaxed mb-6">
              Conheça o especialista responsável por desenvolver uma abordagem que está transformando a forma
              como seguros são contratados no Brasil.
            </p>

            <div className="space-y-3.5 mb-7">
              <p className="text-sm sm:text-base text-petrol-700 leading-relaxed">
                <strong className="text-petrol-900">Paulo Cardoso</strong> é especialista em gestão de riscos
                e contratação de seguros, com mais de{' '}
                <strong className="text-petrol-900">33 anos de atuação</strong> no mercado brasileiro.
              </p>
              <p className="text-sm sm:text-base text-petrol-700 leading-relaxed">
                Ao longo da sua trajetória, acompanhou na prática milhares de processos de contratação,
                renovação e negociação de seguros, identificando padrões que impactam diretamente o custo,
                a qualidade das coberturas e a segurança do segurado.
              </p>
              <p className="text-sm sm:text-base text-petrol-700 leading-relaxed">
                Foi a partir dessa vivência que surgiu o{' '}
                <strong className="text-petrol-900">Método de Contratação Ativa</strong> — uma abordagem
                estruturada que devolve ao segurado o controle da decisão, permitindo contratar melhor,
                com mais proteção e pagando menos.
              </p>
            </div>

            <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-xl px-5 py-4 mb-7">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-double-quotes-l text-teal-500 text-xl" aria-hidden="true" />
                </div>
                <p className="text-sm sm:text-base text-petrol-800 font-medium italic leading-relaxed">
                  "Foi a partir da vivência prática acumulada ao longo de décadas que surgiu o Método de
                  Contratação Ativa — uma abordagem criada para devolver ao segurado o controle da decisão."
                </p>
              </div>
            </div>

            <ul
              ref={listRef as React.RefObject<HTMLUListElement>}
              className="space-y-2.5 mb-8"
              aria-label="Diferenciais de Paulo Cardoso"
            >
              {diferenciais.map((item, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-3 transition-all duration-500 ease-out ${
                    visibleItems[i] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                    <i className={`${item.icon} text-teal-600 text-sm`} aria-hidden="true" />
                  </div>
                  <span className="text-sm sm:text-base text-petrol-800 font-semibold leading-snug">
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>

            <div
              ref={ctaRef as React.RefObject<HTMLDivElement>}
              className={`transition-all duration-700 ease-out ${
                ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <a
                href="#analise-gratuita"
                className="flex sm:inline-flex items-center justify-center gap-2.5 bg-action-orange-500 hover:bg-action-orange-600 active:scale-95 text-white font-bold text-sm sm:text-base lg:text-lg px-7 sm:px-9 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto whitespace-nowrap"
                aria-label="Aplicar o Método de Contratação Ativa"
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-book-open-line text-white text-lg" aria-hidden="true" />
                </div>
                Quero aplicar o Método de Contratação Ativa
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-right-line text-white text-base" aria-hidden="true" />
                </div>
              </a>
              <p className="text-xs text-petrol-400 mt-2.5 sm:text-left text-center">
                Acesso imediato · Aplicação prática · Sem risco
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}